import { z } from "zod";
import { BRIEF_LIMITS } from "./briefLimits.js";
/** Browser-safe contract. The wizard and engine must parse the same submission. */
export function normalizeInspirationUrl(raw) {
    const value = raw.trim();
    if (!value || /^https?:\/\//i.test(value))
        return value;
    return `https://${value}`;
}
export const OptionalEmailSchema = z.preprocess(value => value == null ? undefined : typeof value === "string" ? value.trim() || undefined : value, z.string().email().optional());
export const InspirationUrlSchema = z.preprocess(value => typeof value === "string" ? normalizeInspirationUrl(value) : value, z.string().url().refine(value => /^https?:\/\//i.test(value), "Use an http or https website address"));
export const UploadMetadataSchema = z.object({
    name: z.string().min(1).max(200), mimeType: z.string().min(1).max(200), sizeBytes: z.number().int().min(0),
});
export const BriefAttachmentSchema = UploadMetadataSchema.extend({
    gcsPath: z.string().optional(), url: z.string().url().optional(), tag: z.string().max(60).optional(),
    source: z.enum(["owner-upload", "pbf-harvest"]).optional(),
}).refine(file => !!file.gcsPath || !!file.url, "attachment needs gcsPath or url");
export const BriefDraftSchema = z.object({
    source: z.enum(["questionnaire", "pbf", "hybrid"]),
    business: z.object({
        name: z.string().trim().min(1),
        tagline: z.string().optional(),
        type: z.enum(["restaurant", "retail", "service", "professional", "health", "creative", "other"]),
        description: z.string().trim().min(BRIEF_LIMITS.descriptionMinimum),
        contact: z.object({
            address: z.string().optional(), phone: z.string().optional(), email: OptionalEmailSchema,
        }).optional(),
        hours: z.record(z.object({ open: z.string(), close: z.string(), closed: z.boolean() })).optional(),
        // Preserve provider-specific signals, as the original engine contract did.
        signals: z.object({
            rating: z.number().optional(), reviewCount: z.number().int().optional(), priceLevel: z.number().int().optional(),
            googleMapsUrl: z.string().optional(), services: z.record(z.boolean()).optional(),
            reviews: z.array(z.object({ author: z.string(), rating: z.number(), text: z.string() })).optional(),
            toneProfile: z.object({
                formality: z.number().min(0).max(1), energy: z.number().min(0).max(1),
                polish: z.number().min(0).max(1), intimacy: z.number().min(0).max(1), reasoning: z.string(),
            }).optional(),
        }).passthrough().optional(),
    }),
    capabilities: z.array(z.string()),
    integrations: z.array(z.string()).optional(),
    integrationConfig: z.record(z.record(z.string())).optional(),
    ownerNote: z.string().max(BRIEF_LIMITS.ownerNote).optional(),
    attachments: z.array(BriefAttachmentSchema).max(BRIEF_LIMITS.attachments).optional(),
    inspo: z.array(z.object({
        url: InspirationUrlSchema, weight: z.number().min(0).max(1),
        lockedMoves: z.array(z.enum(["color", "type", "layout"])).optional(),
    })),
    pbfHarvestId: z.string().optional(),
    voice: z.object({
        // Zod strips retired aesthetic answers from older clients.
        motion: z.boolean().optional(),
    }).optional(),
});
// Ownership is added by the authenticated portal, never required from a form.
export const BriefSubmissionSchema = BriefDraftSchema.extend({ ownerId: z.string().min(1) });
export const BriefDetailsSchema = BriefDraftSchema.shape.business.pick({ name: true, description: true });
export const BriefMediaSchema = BriefDraftSchema.pick({ attachments: true, ownerNote: true });
/** Stored briefs predate the submission schema. Strip retired questionnaire
 * guidance before generation without rewriting historical database records. */
export function stripLegacyStyleGuidance(brief) {
    const { voice, ...facts } = brief;
    const motion = voice && typeof voice === "object" ? voice.motion : undefined;
    return { ...facts, ...(typeof motion === "boolean" ? { voice: { motion } } : {}) };
}
